import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import Breadcrumbs from "../../../components/BreadCrumbs";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filter";
import messages from "../../../assets/locale/messages";
import eventPlaceholder from "../../../assets/images/event-screen/events-placeholder.svg";
import {
  textContainOnlySpaces,
  formatDate,
  renderWordEllipsis,
} from "../../../utils/Helpers";
import { eventStatuses, eventTypes } from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import {
  UICard,
  UIPaginationBar,
  UIStatusTag,
  UITable,
  UITabs,
} from "../../../components/ui";
import {
  initialTimeSheetFetch,
  setTimeSheetFilters,
  setTimeSheetPage,
  setTimeSheetSearch,
} from "../../../store/PersistTimeSheet/actions";
import "../Events/Events.scss";
import "./TimeSheet.scss";

const TimeSheet = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { timeSheet, events, shared } = messages[lang];
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.events.meta);
  const list = useSelector((state) => state.events.list);
  const persistState = useSelector((state) => state.persistTimeSheet);

  const [q, setQuery] = useState("");

  const activeStatus =
    persistState.event_status?.value ?? eventStatuses.ongoing;
  const pageSize = persistState.rowsCount || 10;
  const currentPage = meta?.currentPage || persistState.page || 1;

  const eventTypeOptions = [
    { id: 1, name: events.listEvents.daily, value: eventTypes.daily },
    { id: 2, name: events.listEvents.monthly, value: eventTypes.monthly },
  ];

  const timeSheetTabs = [
    {
      value: eventStatuses.ongoing,
      label: events.listEvents.eventTabs.ongoing,
    },
    {
      value: eventStatuses.upcoming,
      label: events.listEvents.eventTabs.upcoming,
    },
    {
      value: eventStatuses.past,
      label: events.listEvents.eventTabs.previous,
    },
  ];

  const timeSheetStatusOptions = {
    [eventStatuses.ongoing]: {
      id: 1,
      value: eventStatuses.ongoing,
      name: events.listEvents.eventTabs.ongoing,
    },
    [eventStatuses.upcoming]: {
      id: 2,
      value: eventStatuses.upcoming,
      name: events.listEvents.eventTabs.upcoming,
    },
    [eventStatuses.past]: {
      id: 3,
      value: eventStatuses.past,
      name: events.listEvents.eventTabs.previous,
    },
  };

  const handleFilterChange = (newValue) => {
    dispatch(setTimeSheetFilters({ ...persistState, ...newValue }));
  };

  const handleSelectTimeSheetTab = (status) => {
    dispatch(
      setTimeSheetFilters({
        ...persistState,
        event_status: timeSheetStatusOptions[status],
        page: 1,
      })
    );
  };

  const handlePageChange = (newPage) => {
    dispatch(setTimeSheetPage(newPage));
  };

  const handlePageSizeChange = (nextSize) => {
    dispatch(
      setTimeSheetFilters({
        ...persistState,
        rowsCount: nextSize,
        page: 1,
      })
    );
  };

  const navigateToReport = useCallback(
    (row, event) => {
      if (event) {
        event.stopPropagation();
      }
      if (!row?.payment_period) return;

      history.push(
        ROUTE_PATHS[
          row.payment_period === eventTypes.daily ? "dailyReport" : "monthlyReport"
        ].replace(":eventid", row.id),
        { event_name: row.name }
      );
    },
    [history]
  );

  const rows = useMemo(
    () =>
      (list || []).map((row) => ({
        ...row,
        id: row.id,
      })),
    [list]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: timeSheet.eventName,
        width: 200,
        flex: 0,
        sortable: false,
        renderCell: (params) =>
          params.value ? renderWordEllipsis(params.value) : "-",
      },
      {
        field: "payment_period",
        headerName: timeSheet.type,
        width: 140,
        sortable: false,
        renderCell: (params) => (
          <div className="MuiDataGrid-cellContent">
            {params.value ? (
              <UIStatusTag
                preset="table"
                status={params.value}
                label={events.listEvents[params.value]}
              />
            ) : (
              "-"
            )}
          </div>
        ),
      },
      {
        field: "status",
        headerName: timeSheet.status,
        width: 130,
        sortable: false,
        renderCell: (params) =>
          events.listEvents.eventStatus[params.value] || "-",
      },
      {
        field: "event_start_at",
        headerName: timeSheet.startDate,
        width: 130,
        sortable: false,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "event_end_at",
        headerName: timeSheet.endDate,
        width: 130,
        sortable: false,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "reportLink",
        headerName: timeSheet.link,
        width: 180,
        sortable: false,
        renderCell: (params) =>
          params.row.payment_period ? (
            <button
              type="button"
              className="timesheet-list-view__report-link"
              onClick={(event) => navigateToReport(params.row, event)}
            >
              {timeSheet.attendanceReport}
            </button>
          ) : (
            "-"
          ),
      },
    ],
    [timeSheet, events, navigateToReport]
  );

  const paginationFrom =
    meta?.count > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(currentPage * pageSize, meta?.count || 0);
  const paginationPages = meta?.pages || 1;

  useEffect(() => {
    dispatch(
      setTimeSheetFilters({
        ...persistState,
        event_status: null,
        page: 1,
      })
    );
  }, []);

  useEffect(() => {
    if ((q.length >= 3 || q.length === 0) && !textContainOnlySpaces(q)) {
      dispatch(setTimeSheetSearch(q));
    }
  }, [q, dispatch]);

  useEffect(() => {
    dispatch(initialTimeSheetFetch());
  }, [lang, persistState, dispatch]);

  const renderEventsTimesheetList = () => {
    if (!isLoading && !rows.length) {
      return (
        <UICard>
          <EmptyState
            description={events.listEvents.noDataDescription}
            imgSrc={eventPlaceholder}
            showActionButton={false}
          />
        </UICard>
      );
    }

    return (
      <>
        <UITable
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          contentSized
          hideFooter
          onRowClick={({ row }) => navigateToReport(row)}
          getRowClassName={(params) =>
            params.row.payment_period ? "timesheet-list-view__row" : ""
          }
        />
        {(meta?.count || 0) > 0 && (
          <UIPaginationBar
            currentPage={currentPage}
            pages={paginationPages}
            pageSize={pageSize}
            rangeTemplate={events.listEvents.showingEventsRange}
            rangeFrom={paginationFrom}
            rangeTo={paginationTo}
            rangeTotal={meta?.count || 0}
            rowsPerPageLabel={shared.numberOfRows}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            className="timesheet-list-view__pagination"
          />
        )}
      </>
    );
  };

  return (
    <div className="timesheet-list-view events-list-container">
      <div className="page-title-margin">
        <Breadcrumbs
          list={[
            {
              path: "",
              label: timeSheet.listTimeSheetBreadcrumb,
            },
          ]}
          BreadCrumbHasBackIcon={false}
        />
        <Box className="d-flex justify-content-between align-items-center">
          <Typography variant="h5">
            {timeSheet.listTimeSheetBreadcrumb}
          </Typography>
        </Box>
      </div>

      <UITabs
        tabs={timeSheetTabs}
        value={activeStatus}
        onChange={handleSelectTimeSheetTab}
        classNames={{ root: "timesheet-list-view__tabs" }}
      />

      <Filters
        searchInput={{
          placeholder: timeSheet.search,
          name: "search",
          value: q,
          onChange: (value) => setQuery(value),
        }}
        filters={[
          {
            type: "filter",
            name: "eventType",
            placeholder: timeSheet.type,
            value: persistState.event_type,
            options: eventTypeOptions,
            onChange: (value) => handleFilterChange({ event_type: value }),
          },
          {
            type: "date",
            name: "startDate",
            placeholder: timeSheet.startDate,
            label: timeSheet.startDate,
            value: persistState.event_start_date,
            onChange: (value) =>
              handleFilterChange({ event_start_date: value }),
          },
          {
            type: "date",
            name: "endDate",
            placeholder: timeSheet.endDate,
            label: timeSheet.endDate,
            value: persistState.event_end_date,
            onChange: (value) => handleFilterChange({ event_end_date: value }),
            helperText:
              persistState.event_end_date !== "" &&
              persistState.event_end_date < persistState.event_start_date
                ? timeSheet.dailyReport.endDateValidation
                : "",
            helperTextClass: "err",
          },
        ]}
      />

      <div className="timesheet-list-view__table-shell">
        {renderEventsTimesheetList()}
      </div>
    </div>
  );
};

export default TimeSheet;
